import { confluencePagesQuery } from '@atlassian/search-client';
import { buildAtlAttributionHeaders } from '@atlassian/search-client/atl-attribution';
import {
	type SearchEntitiesProductARI,
	searchEntitiesQuery,
} from '@atlassian/search-client/entities';
import {
	confluenceDcContentFilterOptionsQuery,
	confluenceDcPagesQuery,
	documentARIEntitiesQuery,
} from '@atlassian/search-client/search-page';
import { cleanExcerpt } from '@atlassian/search-common';

import { DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI } from '../../../../../common/constants/entities';
import { type CloudConfig } from '../../../../../common/constants/filters/cloud-config/types';
import type { SelectFilterState } from '../../../../../common/constants/filters/select-filter/types';
import {
	ProductKeys,
	type ProductKeys1P,
	type ProductKeys3P,
} from '../../../../../common/constants/products';
import { getProducts3PFromConfigForFilter } from '../../../../../common/utils/filters';
import { createSingleSelectFilter } from '../../../../filters/single-select-filter';

const getContentId = (ari: string) => {
	const EXTRACT_ID = /ari:.*?:.*?:.*?:.*?\/(.*)?.*/;
	const matches = ari.match(EXTRACT_ID);
	// content id of the ari
	return matches && matches.length > 1 ? matches[1] : '';
};

const searchContent = async (query: string, config: CloudConfig) => {
	const location: SearchEntitiesProductARI = `ari:cloud:confluence::site/${config.cloudId}`;
	const headers = buildAtlAttributionHeaders({
		cloudId: config.cloudId,
		activationId: config.activationId,
		product: config.primaryProduct,
	});

	const product = config.product;
	const selectedSpaceAris = getSelectedSpaceAris(config);
	if (isGoogleDriveContentProduct(product)) {
		const selectedSpaceAri = selectedSpaceAris[0];
		const datasource =
			config.selectedDatasource ?? config.thirdPartyConfigs?.[ProductKeys.GoogleDrive];
		if (!datasource?.integrationAri) {
			return [];
		}
		const response = await searchEntitiesQuery({
			aggAbsoluteUrl: config.aggAbsoluteUrl,
			variables: {
				query,
				first: 10,
				experience: 'search-page',
				filters: {
					entities: DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
					locations: [location],
					thirdPartyFilters: {
						titleMatchOnly: true,
						...(selectedSpaceAri ? { containerAris: [selectedSpaceAri] } : {}),
						thirdPartyProducts: [
							{
								product: ProductKeys.GoogleDrive,
								integrationId: datasource.integrationAri,
								subtypes: ['folder'],
							},
						],
					},
				},
			},
			headers,
		});

		return (
			response.data?.search?.results.edges.map((content) => ({
				trackingKey: content.node.id,
				value: content.node.id,
				label: content.node.title,
				queryParamValue: content.node.id,
				product: config.product,
			})) || []
		);
	}

	let contentPagesResponse;
	if (product === ProductKeys.ConfluenceDC) {
		contentPagesResponse = await confluenceDcContentFilterOptionsQuery({
			variables: {
				query,
				cloudIdARI: location,
				limit: 10,
				entities: ['ati:cloud:graph:document'],
				product,
			},
			aggAbsoluteUrl: config.aggAbsoluteUrl,
			headers,
		});

		return (
			contentPagesResponse.data?.search?.search?.edges.map((content) => ({
				trackingKey: content.node.id,
				value: content.node.id,
				label: cleanExcerpt(content.node.title),
				queryParamValue: content.node.id,
				product: config.product,
			})) || []
		);
	}

	contentPagesResponse = await searchEntitiesQuery({
		aggAbsoluteUrl: config.aggAbsoluteUrl,
		variables: {
			query,
			first: 10,
			filters: {
				entities: ['ati:cloud:confluence:page'],
				locations: [location],
			},
		},
		headers,
	});

	const allContentPages =
		contentPagesResponse.data?.search?.results.edges.map((content) => ({
			title: content.node.title,
			id: content.node.id,
		})) || [];

	const contentOptions = allContentPages.map((content) => {
		const contentId = getContentId(content.id);
		return {
			trackingKey: contentId,
			value: contentId,
			label: content.title,
			queryParamValue: contentId,
			product: config.product,
		};
	});

	return contentOptions;
};

const productKeys1P: ProductKeys1P[] = [ProductKeys.Confluence];

const products3PFromConfig: ProductKeys3P[] = getProducts3PFromConfigForFilter('content');
const isGoogleDriveContentProduct = (product?: string): product is ProductKeys3P =>
	product === ProductKeys.GoogleDrive && products3PFromConfig.includes(ProductKeys.GoogleDrive);
const getSelectedSpaceAris = (config: CloudConfig): string[] =>
	Array.isArray(config.initialSpaceARIs) ? config.initialSpaceARIs.filter(Boolean) : [];

export const filter: Readonly<{
	id: 'content';
	type: 'single-select' | 'multi-select';
	queryParams: {
		key: string;
		getState: (
			queryParams: Record<string, string[]>,
			cloudId: string,
		) => SelectFilterState<'content'>;
	};
	products: ProductKeys[];
	universal: boolean;
	customValueFields?: string[];
	withState: (
		state: unknown,
		config: CloudConfig,
	) => import('../../../../../common/constants/filters/select-filter/types').SelectFilterMonad<'content'>;
	getInitialState: (
		config: CloudConfig,
	) => import('../../../../../common/constants/filters/select-filter/types').SelectFilterMonad<'content'>;
	isCompatible: (filterId?: string) => boolean;
	isSupportCustomValue: boolean;
	isSuggestedFilter?: (primaryProduct?: string) => boolean;
}> = createSingleSelectFilter({
	id: 'content',
	products: [...productKeys1P, ...products3PFromConfig],
	alwaysVisible: false,
	universal: false,
	queryParams: {
		key: 'ancestor',
		fetcher: async (queryParams, config) => {
			const product = config.product;
			const location: SearchEntitiesProductARI = `ari:cloud:confluence::site/${config.cloudId}`;
			const headers = buildAtlAttributionHeaders({
				cloudId: config.cloudId,
				activationId: config.activationId,
				product: config.primaryProduct,
			});

			if (isGoogleDriveContentProduct(product)) {
				const contentResponse = await documentARIEntitiesQuery({
					variables: { ids: queryParams },
					aggAbsoluteUrl: config.aggAbsoluteUrl,
					headers,
				});

				return (
					contentResponse.data?.external_entities?.document?.map((content) => ({
						trackingKey: content.id,
						value: content.id,
						label: content.displayName,
						queryParamValue: content.id,
						product: config.product,
					})) || []
				);
			}

			if (product === ProductKeys.ConfluenceDC) {
				const contentPagesResponse = await confluenceDcPagesQuery({
					variables: {
						query: '',
						cloudIdARI: location,
						limit: 10,
						entities: ['ati:cloud:graph:document'],
						product,
						ancestorIds: queryParams,
					},
					aggAbsoluteUrl: config.aggAbsoluteUrl,
					headers,
				});

				return (
					contentPagesResponse.data?.external_entities?.document?.map((content) => ({
						trackingKey: content.id,
						value: content.id,
						label: content.displayName,
						queryParamValue: content.id,
						product: config.product,
					})) || []
				);
			}

			const initialContentARIs = queryParams.map(
				(contentId) => `ari:cloud:confluence:${config.cloudId}:page/${contentId}`,
			);

			const initialContentResponse = await confluencePagesQuery(initialContentARIs, headers);

			const allContentPages =
				initialContentResponse.data?.confluence.pages.map((page) => ({
					title: page.title,
					id: page.id,
				})) || [];

			const contentOptions = allContentPages.map((content) => {
				const contentId = getContentId(content.id);
				return {
					trackingKey: contentId,
					value: contentId,
					label: content.title,
					queryParamValue: contentId,
					product: config.product,
				};
			});
			return contentOptions;
		},
	},
	initialOptions: {
		fetcher: async (config) => {
			return searchContent('', config);
		},
	},
	lookup: {
		fetcher: async (query, config) => {
			return searchContent(query, config);
		},
	},
});
